// ═══════════════════════════════════════════════════════════════════════════════
// POST /api/receipts/analyze — sequential uploads + retry (network only)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  AI_ANALYZE_FETCH_TIMEOUT_MS,
  AI_MAX_RETRY_COUNT,
  AI_RETRY_WAIT_MS,
} from '@fdh/shared';
import type { AnalyzeResponse } from '@fdh/shared';
import { getStoredToken } from './api';
import { emitLimitReachedFromHttp } from './limitError';

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const isRetriableNetworkFailure = (e: unknown): boolean => {
  if (e instanceof TypeError) return true;
  if (e instanceof DOMException && e.name === 'AbortError') return true;
  return false;
};

export class AnalyzeHttpError extends Error {
  readonly status: number;
  readonly body: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    super(typeof body.error === 'string' ? body.error : `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

const postAnalyzeOnce = async (formData: FormData): Promise<AnalyzeResponse> => {
  const token = getStoredToken();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_ANALYZE_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch('/api/receipts/analyze', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    });

    const json = (await response.json()) as Record<string, unknown>;

    if (!response.ok) {
      emitLimitReachedFromHttp(response.status, json);
      throw new AnalyzeHttpError(response.status, json);
    }

    if (json.success !== true || json.data === undefined || json.data === null) {
      throw new Error(typeof json.error === 'string' ? json.error : 'Analyze failed');
    }

    return json.data as AnalyzeResponse;
  } finally {
    clearTimeout(timeoutId);
  }
};

const postAnalyzeWithRetries = async (formData: FormData): Promise<AnalyzeResponse> => {
  const maxAttempts = AI_MAX_RETRY_COUNT + 1;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      const waitMs = AI_RETRY_WAIT_MS[attempt - 1] ?? 1000;
      await sleep(waitMs);
    }
    try {
      return await postAnalyzeOnce(formData);
    } catch (e) {
      if (e instanceof AnalyzeHttpError) {
        throw e;
      }
      if (!isRetriableNetworkFailure(e) || attempt === maxAttempts - 1) {
        throw e;
      }
    }
  }
  throw new Error('Analyze failed');
};

export const analyzeReceiptsSequential = async (
  items: Array<{ blob: Blob; fileName: string }>,
  onProgress?: (current: number, total: number) => void
): Promise<AnalyzeResponse> => {
  const results: AnalyzeResponse['results'] = [];
  const total = items.length;

  for (let i = 0; i < items.length; i += 1) {
    onProgress?.(i + 1, total);
    const fd = new FormData();
    fd.append('files', items[i].blob, items[i].fileName);
    const part = await postAnalyzeWithRetries(fd);
    results.push(...part.results);
  }

  return { totalFiles: results.length, results };
};
